import React, { useContext, useRef, useState } from 'react'
import { chatbotTriggerConfigs, condition_group_trigger_configs, triggerConfigs } from '../../../../../../models/flowBuilderTypes';
import { WorkflowContext, WorkflowContextPayload } from '../../../../../../context/workflowContext';
import { FlowBuilderContext, FlowBuilderContextPayload } from '../../../../../../context/flowBuilderContext';
import { Divider, Grid, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import globalStyles from "../../../../../../assets/styles/global.module.scss";
import style from "../../../../../../assets/styles/componentStyles/campaignContactStyles/campaignContactStyle.module.scss";
import dashboardStyles from '../../../../../../assets/styles/componentStyles/callAnalysisStyles/callAnalysisDashboard.module.scss';
import workflowStyles from '../../../../../../assets/styles/componentStyles/manychatStyles/workflowFormComponents/workflowForm.module.scss'
import globalStrenameDocumentyles from "../../../../../../assets/renameDocument.svg";
import deleteDoc from "../../../../../../assets/deleteDoc.svg"; 
import { useVirtualizer } from '@tanstack/react-virtual';
import MemoizedSmartDelayTableRow from './memoizedSmartDelayTableRow';
import { useFlowBuilderStore } from '../../../../../../store/flowBuilderStore';
import GlobalButton from '../../../../../utility/globalButton/button';
import AddIcon from '@mui/icons-material/Add';
import { generateUUID } from '../../../../../../utils/constants';
import { emptyDelayObject, isCurrentViewVersionPublished } from '../../../utilityFunctions';
import GlobalDeletePopup from '../../../../../utility/globalDeletePopup/globalDeletePopup';
import { getCurrentViewVersionsTriggers, getCustomVariableById } from '../../../flowBuilderConstants';

interface SmartDelayTablePopupProps {
    getStepTriggers:  () => triggerConfigs[],
    selectedTrigger: triggerConfigs | null,
    setSelectedTrigger: React.Dispatch<React.SetStateAction<triggerConfigs | null>>,
    setFlagSmartDelayPopup: React.Dispatch<React.SetStateAction<boolean>>,
    updateChatbotTriggerConfigs: (trigger_configs: triggerConfigs[]) => Promise<void>,
    getStepNameByStepId: (id: string, defaultValue: string) => string, 
    flagStepLevel: boolean,
    stepId?: string,
}

const SmartDelayTablePopup: React.FC<SmartDelayTablePopupProps> = (props) => {
    
    const {getStepTriggers, selectedTrigger, setSelectedTrigger, setFlagSmartDelayPopup, updateChatbotTriggerConfigs, flagStepLevel, getStepNameByStepId, stepId} = props;

    const { currentActiveStaticWorkflow } = useContext(WorkflowContext) as WorkflowContextPayload; 
    const { nodes } = useContext(FlowBuilderContext) as FlowBuilderContextPayload
    const customVariableList = useFlowBuilderStore((state) => state.customVariableList) 
    const chatbotTriggerConfigs = useFlowBuilderStore((store) => store.chatbotTriggerConfigs);

    const innerHeight = (window.innerHeight - 300);
    const tableContainerRef = useRef<HTMLDivElement>(null);

    const [anchorEl, setAnchorEl] = useState<null | SVGSVGElement>(null);
    const [currentRowIndex, setCurrentRowIndex] = useState<string | null>(null);

    const [flagShowDeleteConfirmation, setFlagShowDeleteConfirmation] = useState<boolean>(false);

    const handleMenuOpen = (event: React.MouseEvent<SVGSVGElement, MouseEvent>, rowIndex: string) => {
        setCurrentRowIndex(rowIndex);
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setCurrentRowIndex(null);
        setAnchorEl(null);
    };

    const menuItem = [
        {
            label: "Edit",
            icon: <img alt="" src={globalStrenameDocumentyles} />,
            isDisable: isCurrentViewVersionPublished(currentActiveStaticWorkflow),
            action: (data: triggerConfigs) => {
                setSelectedTrigger(data);
                setFlagSmartDelayPopup(true);
            }
        },
        {
            label: "Delete",
            icon: <img alt="" src={deleteDoc} />,
            isDisable: isCurrentViewVersionPublished(currentActiveStaticWorkflow),
            action: (data: triggerConfigs) => {
                setSelectedTrigger(data);
                setFlagShowDeleteConfirmation(true);
            }
        }
    ]

    const virtualizer = useVirtualizer({
        count: getStepTriggers()?.length ?? 0,
        getScrollElement: () => tableContainerRef.current,
        estimateSize: () => 48,
        overscan: 10,
        measureElement:
            typeof window !== 'undefined' && !navigator.userAgent.includes('Firefox')
                ? el => el?.getBoundingClientRect().height
                : undefined,
    });

    const deleteSmartDelay = async (selectedTrigger: triggerConfigs | null) => {
        if(selectedTrigger){
            let trigger_configs: triggerConfigs[] = [];
            const triggers: chatbotTriggerConfigs | undefined = getCurrentViewVersionsTriggers(chatbotTriggerConfigs, currentActiveStaticWorkflow);
            if(triggers){
                trigger_configs = triggers?.trigger_configs?.filter((trigger) => trigger?.chatbot_timeout_trigger_config_id != selectedTrigger?.chatbot_timeout_trigger_config_id);
            }
            await updateChatbotTriggerConfigs(trigger_configs);
        }
    }

    const getFullConditionText = (data: triggerConfigs) => {
        const { custom_variable_conditions } =  data?.condition_group ?? {};
        return `${getCustomVariableById(customVariableList, custom_variable_conditions?.chatbot_custom_variable_configuration_id)?.chatbot_custom_variable_name ?? "-"} ${custom_variable_conditions?.operator_type_name ?? ""} ${custom_variable_conditions?.condition_value ?? ""}`
    }

    const getSmartDelayPopupTitle = () => {
        if(flagStepLevel){
            return `Smart Delay at ${getStepNameByStepId(stepId ?? "", "This Step")}`;
        } else {
            return "Smart Delay at Chatbot Level"; 
        }
    }
    
    const hasVirtualItems = () => {
        return virtualizer.getVirtualItems().length > 0;
    }
    return (
        <>
            <Grid container>
                <Grid item xs={12}>
                    <Grid container className={`${globalStyles.flexColumn} ${globalStyles.justifyCenter}`}>
                        <Grid item className={globalStyles.textCenter}>
                            <Typography className={style.popupTitleTypography}>
                                {getSmartDelayPopupTitle()}
                            </Typography>
                        </Grid>

                        <Divider className={style.popupTitleDivider} />  

                        <Grid item className={globalStyles.fullwidth}>
                            <Grid container className={style.popupBodyGridContainer} gap={2}>
                                
                                <Grid item className={globalStyles.fullwidth} xs={12}>
                                    <TableContainer className={`${style.contactTableContainer} ${globalStyles.overflowAuto} ${globalStyles.relativePosition}`} ref={tableContainerRef} sx={{ maxHeight: `${innerHeight}px !important` }}>
                                        <Table stickyHeader>
                                            <TableHead className={workflowStyles.smartDelayTableHead}>
                                                <TableRow className={`${globalStyles.fullwidth} ${globalStyles.displayFlexStyle}`}> 
                                                    <TableCell className={`${dashboardStyles.tableBodyHeadingFontStyle} ${style.fieldCellWidth}`}>
                                                        Delay Time
                                                    </TableCell>
                                                    <TableCell className={`${dashboardStyles.tableBodyHeadingFontStyle} ${style.fieldCellWidth}`}>
                                                        Redirect Chatbot Step
                                                    </TableCell>
                                                    <TableCell className={`${dashboardStyles.tableBodyHeadingFontStyle} ${style.fieldCellWidth}`}>
                                                        Flag Trigger on Inactivity
                                                    </TableCell>
                                                    <TableCell className={`${dashboardStyles.tableBodyHeadingFontStyle} ${style.fieldCellWidth}`}>
                                                        Delay Condition
                                                    </TableCell> 
                                                    <TableCell className={`${dashboardStyles.tableBodyHeadingFontStyle} ${style.lastColumnSticky} ${style.checkBoxCellWidth} ${globalStyles.tertiaryBgColor}`}></TableCell>
                                                </TableRow>
                                            </TableHead>

                                            <TableBody
                                                sx={{
                                                    display: hasVirtualItems() ? 'block' : 'contents',
                                                    height: hasVirtualItems() ? `${virtualizer.getTotalSize()}px` : 'auto',
                                                    position: hasVirtualItems() ? 'relative' : 'static',
                                                }}
                                            >
                                                {hasVirtualItems() ?
                                                    virtualizer.getVirtualItems().map(virtualRow => {
                                                        const data = getStepTriggers()?.[virtualRow.index];
                                                        if (!data) return null;

                                                        return (
                                                            <TableRow
                                                                key={data.chatbot_timeout_trigger_config_id}
                                                                data-index={virtualRow.index}
                                                                ref={node => virtualizer.measureElement(node)}
                                                                style={{ transform: `translateY(${virtualRow.start}px)`}}
                                                                className={`${globalStyles.tableRowGlobalStyle} ${workflowStyles.memoizedSMartDelayTableRow}`}
                                                            > 
                                                                <MemoizedSmartDelayTableRow
                                                                    data={data} 
                                                                    index={virtualRow?.index} 
                                                                    anchorEl={anchorEl}
                                                                    currentRowIndex={currentRowIndex}
                                                                    menuItem={menuItem}
                                                                    handleMenuOpen={handleMenuOpen}
                                                                    handleMenuClose={handleMenuClose}  
                                                                    getFullConditionText={getFullConditionText}
                                                                    getStepNameByStepId={getStepNameByStepId}
                                                                    isMenuOpen={data.chatbot_timeout_trigger_config_id == currentRowIndex}  
                                                                />
                                                            </TableRow>
                                                        );
                                                    })
                                                    :
                                                    <TableRow className={`${dashboardStyles.cellRow}`}>
                                                        <TableCell colSpan={12} className={globalStyles.textCenter}>
                                                            No Data Available
                                                        </TableCell>
                                                    </TableRow>
                                                }
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </Grid>
                                
                                <Grid item className={globalStyles.fullwidth} xs={12}>
                                    <GlobalButton
                                        buttonText="Smart Delay"
                                        className='primaryButtonStyle'
                                        startIcon={<AddIcon />}
                                        sx={{ width: "150px" }}
                                        disabled={isCurrentViewVersionPublished(currentActiveStaticWorkflow)}
                                        onClick={() => {
                                            setSelectedTrigger(emptyDelayObject(generateUUID(), flagStepLevel, flagStepLevel ? stepId : null));
                                            setFlagSmartDelayPopup(true);
                                        }}
                                    />
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
            {flagShowDeleteConfirmation &&
                <GlobalDeletePopup
                    deleteDescription={`Are you sure you want to delete this smart delay`}
                    open={flagShowDeleteConfirmation}
                    onClose={() => { setFlagShowDeleteConfirmation(false) }}
                    onDelete={() => {
                        deleteSmartDelay(selectedTrigger);
                        setFlagShowDeleteConfirmation(false)
                    }}
                />
            }
        </>
    )
}

export default SmartDelayTablePopup
